import {
  FieldOption,
  LogicalOperator,
  OperatorOption,
} from '@/components/publicSearch/constants';
import type {
  FilterGroup,
  FilterInput,
} from '@/components/publicSearch/constants';
import type {
  AnimalEbvFilterField,
  AnimalEbvFilteredFilterOptionInput,
  ComparisonOperator,
  FilterGroupOfAnimalEbvFilteredFilterOptionInput,
  LogicalOperator as GqlLogicalOperator,
} from '@/graphqlClient/types';
import { normalizeLegacyPercentileFilters } from '@/components/publicSearch/search/ebvPercentileFilterHelpers';
import { expandCalvingYearTokens } from '@/components/publicSearch/search/components/CalvingYearChipInput';
import { tryExpandFilterInputToComparisonRows } from '@/components/publicSearch/search/searchFilterEbvExpansion';
import {
  isIdentChipQuickSearchField,
  isIdentChipStyleFilter,
} from '@/components/publicSearch/search/animalIdentChipFilter';
import {
  processIdentChipFilterToGql,
  type GqlFilterSubGroup,
} from '@/components/publicSearch/search/identChipGql';
import { transformFieldName } from './animalEbvGrid';
import {
  ANIMAL_LOOKUP_FIELD_PREFIX,
  getAnimalLookupResolverFromSearchSettings,
} from '@/components/publicSearch/search/constants';
import { expandI6LookupMarkerFilters } from '@/components/publicSearch/search/expandI6LookupFilters';

type SettingsArray =
  | Array<{ societySettingType?: string | null; value?: string | null }>
  | null
  | undefined;

/** Same condition as `animalsOnSaleOnly` when applied via GraphQL (sale count > 0). */
const ACTIVE_SALE_LISTINGS_TOGGLE_FILTER_INPUT: FilterInput = {
  field: FieldOption.saleCount.value,
  operator: OperatorOption.greaterThan.value,
  value: '0',
};

function isActiveSaleListingsToggleFilterInput(
  f: Pick<FilterInput, 'field' | 'operator' | 'value'>,
): boolean {
  return (
    f.field === ACTIVE_SALE_LISTINGS_TOGGLE_FILTER_INPUT.field &&
    f.operator === ACTIVE_SALE_LISTINGS_TOGGLE_FILTER_INPUT.operator &&
    f.value === ACTIVE_SALE_LISTINGS_TOGGLE_FILTER_INPUT.value
  );
}

function filterGroupsHaveActiveSaleToggleRow(groups: FilterGroup[]): boolean {
  return groups.some((g) =>
    g.filters.some((row) => isActiveSaleListingsToggleFilterInput(row)),
  );
}

function filtersHaveActiveSaleToggleRow(filters: FilterInput[]): boolean {
  return filters.some((row) => isActiveSaleListingsToggleFilterInput(row));
}

function mapIdentChipSubGroup(
  sg: GqlFilterSubGroup,
): FilterGroupOfAnimalEbvFilteredFilterOptionInput {
  return {
    filters: sg.filters.map((f) => ({
      field: f.field as AnimalEbvFilterField,
      operator: f.operator as ComparisonOperator,
      value: f.value,
    })),
    groups: sg.groups?.map(mapIdentChipSubGroup),
    logicalOperator: sg.logicalOperator as GqlLogicalOperator,
  };
}

function parseMultiSelectValues(value: string) {
  return Array.from(
    new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
}

/**
 * Processes a flat array of FilterInput into GraphQL-compatible filters and
 * any sub-groups that arise from multi-select / range expansion.
 */
function processFiltersToGql(filters: FilterInput[]): {
  processed: AnimalEbvFilteredFilterOptionInput[];
  subGroups: FilterGroupOfAnimalEbvFilteredFilterOptionInput[];
} {
  const processed: AnimalEbvFilteredFilterOptionInput[] = [];
  const subGroups: FilterGroupOfAnimalEbvFilteredFilterOptionInput[] = [];

  const normalizedFilters = normalizeLegacyPercentileFilters(filters);

  for (const { field, operator, value } of normalizedFilters) {
    if (!field) continue;

    // Animal / dam / sire chip-style picker: each chip carries its own
    // wildcard pattern that maps to a comparison operator. A single chip
    // emits one comparison row; multiple chips become an OR sub-group.
    // Legacy single-value rows (no comma, no `%`) fall through to the
    // generic path so existing `(operator, value)` filters are unaffected.
    if (
      isIdentChipQuickSearchField(field) &&
      typeof value === 'string' &&
      isIdentChipStyleFilter(operator, value)
    ) {
      const { processed: chipRows, subGroups: chipGroups } =
        processIdentChipFilterToGql(field, value, transformFieldName);
      if (chipRows.length === 0 && chipGroups.length === 0) continue;
      for (const row of chipRows) {
        processed.push({
          field: row.field as AnimalEbvFilterField,
          operator: row.operator as ComparisonOperator,
          value: row.value,
        });
      }
      for (const sg of chipGroups) {
        subGroups.push(mapIdentChipSubGroup(sg));
      }
      continue;
    }

    if (
      field === FieldOption.sex.value &&
      operator &&
      typeof value === 'string'
    ) {
      const selectedValues = parseMultiSelectValues(value);
      if (selectedValues.length > 1) {
        subGroups.push({
          filters: selectedValues.map((selectedValue) => ({
            field: transformFieldName(field) as AnimalEbvFilterField,
            operator: operator as ComparisonOperator,
            value: selectedValue,
          })),
          logicalOperator:
            operator === OperatorOption.notEquals.value
              ? LogicalOperator.and
              : LogicalOperator.or,
        });
        continue;
      }
    }

    if (
      field === FieldOption.calvingYear.value &&
      operator &&
      typeof value === 'string' &&
      value.includes(',')
    ) {
      const tokens = value
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const expandedYears = expandCalvingYearTokens(tokens);
      if (expandedYears.length > 1) {
        subGroups.push({
          filters: expandedYears.map((year) => ({
            field: transformFieldName(field) as AnimalEbvFilterField,
            operator: OperatorOption.equals.value as ComparisonOperator,
            value: year,
          })),
          logicalOperator: LogicalOperator.or,
        });
        continue;
      }
      if (expandedYears.length === 1) {
        processed.push({
          field: transformFieldName(field) as AnimalEbvFilterField,
          operator: OperatorOption.equals.value as ComparisonOperator,
          value: expandedYears[0],
        });
        continue;
      }
    }

    if (
      field === FieldOption.calvingYear.value &&
      operator &&
      typeof value === 'string' &&
      /^\d{4}-\d{4}$/.test(value)
    ) {
      const expandedYears = expandCalvingYearTokens([value]);
      if (expandedYears.length > 1) {
        subGroups.push({
          filters: expandedYears.map((year) => ({
            field: transformFieldName(field) as AnimalEbvFilterField,
            operator: OperatorOption.equals.value as ComparisonOperator,
            value: year,
          })),
          logicalOperator: LogicalOperator.or,
        });
        continue;
      }
    }

    const expandedRows = tryExpandFilterInputToComparisonRows({
      field,
      operator,
      value,
    });
    if (expandedRows !== null) {
      for (const row of expandedRows) {
        processed.push({
          field: transformFieldName(row.field) as AnimalEbvFilterField,
          operator: row.operator as ComparisonOperator,
          value: row.value,
        });
      }
      continue;
    }

    if (
      field &&
      operator &&
      (value || operator === OperatorOption.isNotEmpty.value)
    ) {
      processed.push({
        field: transformFieldName(field) as AnimalEbvFilterField,
        operator: operator as ComparisonOperator,
        value: typeof value === 'string' ? value : String(value),
      });
    }
  }

  return { processed, subGroups };
}

function expandAnimalLookupFilters(
  filters: FilterInput[],
  settings?: SettingsArray,
): ReturnType<typeof expandI6LookupMarkerFilters> {
  const resolver = getAnimalLookupResolverFromSearchSettings(settings);
  return expandI6LookupMarkerFilters(
    filters,
    resolver,
    ANIMAL_LOOKUP_FIELD_PREFIX,
  );
}

function lookupGroupsToGqlSubGroups(
  groups: FilterInput[][],
  logicalOperator: GqlLogicalOperator,
): FilterGroupOfAnimalEbvFilteredFilterOptionInput[] {
  return groups.map((group) => ({
    filters: group.map((f) => ({
      field: transformFieldName(f.field) as AnimalEbvFilterField,
      operator: f.operator as ComparisonOperator,
      value: f.value,
    })),
    logicalOperator,
  }));
}

function buildOwnerGroup(
  myAnimals: boolean,
  myAnimalsMemberIds: number[],
): FilterGroupOfAnimalEbvFilteredFilterOptionInput[] {
  if (!myAnimals || myAnimalsMemberIds.length === 0) return [];
  return [
    {
      filters: myAnimalsMemberIds.map((id) => ({
        field: 'CURRENT_OWNER_ID' as AnimalEbvFilterField,
        operator: 'EQUALS' as ComparisonOperator,
        value: String(id),
      })),
      logicalOperator: LogicalOperator.or,
    },
  ];
}

function buildOnSaleListingsGroup(
  animalsOnSaleOnly: boolean,
): FilterGroupOfAnimalEbvFilteredFilterOptionInput[] {
  if (!animalsOnSaleOnly) return [];
  return [
    {
      filters: [
        {
          field:
            ACTIVE_SALE_LISTINGS_TOGGLE_FILTER_INPUT.field as AnimalEbvFilterField,
          operator:
            ACTIVE_SALE_LISTINGS_TOGGLE_FILTER_INPUT.operator as ComparisonOperator,
          value: ACTIVE_SALE_LISTINGS_TOGGLE_FILTER_INPUT.value,
        },
      ],
      logicalOperator: LogicalOperator.and,
    },
  ];
}

export function buildAnimalFilterGroups(
  filters: FilterInput[],
  commonFilterOperator: GqlLogicalOperator | string,
  myAnimals: boolean,
  myAnimalsMemberIds: number[],
  orgId: string | undefined,
  animalsOnSaleOnly = false,
  settings?: SettingsArray,
): FilterGroupOfAnimalEbvFilteredFilterOptionInput[] | null {
  if (!orgId) {
    return null;
  }

  const {
    filters: lookupExpanded,
    andGroups,
    orGroups,
  } = expandAnimalLookupFilters(filters, settings);
  const { processed, subGroups } = processFiltersToGql(lookupExpanded);
  const lookupSubGroups = [
    ...lookupGroupsToGqlSubGroups(andGroups, LogicalOperator.and),
    ...lookupGroupsToGqlSubGroups(orGroups, LogicalOperator.or),
  ];
  const ownerGroup = buildOwnerGroup(myAnimals, myAnimalsMemberIds);
  const onSaleGroup = buildOnSaleListingsGroup(
    animalsOnSaleOnly && !filtersHaveActiveSaleToggleRow(filters),
  );

  const filter: FilterGroupOfAnimalEbvFilteredFilterOptionInput = {
    filters: [],
    groups: [
      {
        filters: processed,
        logicalOperator: commonFilterOperator as GqlLogicalOperator,
        groups: lookupSubGroups.length > 0 ? lookupSubGroups : undefined,
      },
      ...subGroups,
      ...ownerGroup,
      ...onSaleGroup,
    ],
    logicalOperator: LogicalOperator.and,
  };

  return [filter];
}

export function buildAnimalFilterGroupsFromGroups(
  filterGroups: FilterGroup[],
  groupsOperator: GqlLogicalOperator | string,
  myAnimals: boolean,
  myAnimalsMemberIds: number[],
  orgId: string | undefined,
  animalsOnSaleOnly = false,
  settings?: SettingsArray,
): FilterGroupOfAnimalEbvFilteredFilterOptionInput[] | null {
  if (!orgId) {
    return null;
  }

  const gqlGroups: FilterGroupOfAnimalEbvFilteredFilterOptionInput[] =
    filterGroups.map((group) => {
      const {
        filters: lookupExpanded,
        andGroups,
        orGroups,
      } = expandAnimalLookupFilters(group.filters, settings);
      const { processed, subGroups } = processFiltersToGql(lookupExpanded);
      const lookupSubGroups = [
        ...lookupGroupsToGqlSubGroups(andGroups, LogicalOperator.and),
        ...lookupGroupsToGqlSubGroups(orGroups, LogicalOperator.or),
      ];

      if (subGroups.length === 0 && lookupSubGroups.length === 0) {
        return {
          filters: processed,
          logicalOperator: group.logicalOperator as GqlLogicalOperator,
        };
      }

      const innerGroups: FilterGroupOfAnimalEbvFilteredFilterOptionInput[] = [
        {
          filters: processed,
          logicalOperator: group.logicalOperator as GqlLogicalOperator,
          ...(lookupSubGroups.length > 0 ? { groups: lookupSubGroups } : {}),
        },
        ...subGroups,
      ];

      return {
        filters: [],
        groups: innerGroups,
        logicalOperator: group.logicalOperator as GqlLogicalOperator,
      };
    });

  const ownerGroup = buildOwnerGroup(myAnimals, myAnimalsMemberIds);
  const onSaleGroup = buildOnSaleListingsGroup(
    animalsOnSaleOnly && !filterGroupsHaveActiveSaleToggleRow(filterGroups),
  );

  const filter: FilterGroupOfAnimalEbvFilteredFilterOptionInput = {
    filters: [],
    groups: [...gqlGroups, ...ownerGroup, ...onSaleGroup],
    logicalOperator: groupsOperator as GqlLogicalOperator,
  };

  return [filter];
}
